/**
 * The one presentational banner control both web surfaces reuse: the create
 * flow's "Pool basics" step (WI-W3) and the pool Settings "Pool" card
 * (WI-W4). Drives the shipped `usePoolBannerUpload` hook and implements no
 * upload of its own; the confirm around `onRemove` lives at the call site.
 *
 * A picked banner is framed first in the 3:1 crop editor, and only the
 * framed file is uploaded (ISSUE-1094). A file the hook would refuse anyway
 * (wrong type, empty, over the byte cap) skips the editor and reaches the hook,
 * which refuses it locally exactly as before.
 */
import { Button, ImageCropEditor, InlineBanner } from "@sportsmet/ui";
import { type ReactNode, useEffect, useRef, useState } from "react";

import {
  CROP_OUTPUT_MAX_EDGE_PX,
  POOL_BANNER_CROP_ASPECT,
  POOL_BANNER_CROP_TITLE,
} from "../imageCrop/imageCropPresets";
import { shouldOpenCropEditor } from "../imageCrop/shouldOpenCropEditor";
import {
  POOL_BANNER_ADD_LABEL,
  POOL_BANNER_CHANGE_LABEL,
  POOL_BANNER_EMPTY_MESSAGE,
  POOL_BANNER_LABEL,
  POOL_BANNER_PENDING_WITH_BANNER_MESSAGE,
  POOL_BANNER_PENDING_WITH_NONE_MESSAGE,
  POOL_BANNER_REMOVE_LABEL,
  POOL_BANNER_UPLOAD_FAILED_MESSAGE,
  POOL_BANNER_UPLOADING_LABEL,
} from "./poolBannerCopy";
import {
  MAX_POOL_BANNER_BYTES,
  POOL_BANNER_CONTENT_TYPES,
  usePoolBannerUpload,
} from "./usePoolBannerUpload";

export type typePoolBannerFieldProps = {
  /** The pool's current banner URL from POST /pool/get, or null for the empty state. */
  currentBannerUrl: string | null;
  /**
   * Called with the new mediaId once /upload/complete answered published,
   * which it does for every upload whatever the scan concluded (ISSUE-944).
   */
  onBannerReady: (mediaId: string) => void;
  /** Omitted on the create flow, where there is nothing saved yet to remove. */
  onRemove?: (() => void) | undefined;
  /** True while the caller's own save is in flight. Disables the buttons. */
  isSaving?: boolean | undefined;
  testId: string;
};

export const PoolBannerField = (props: typePoolBannerFieldProps): ReactNode => {
  const upload = usePoolBannerUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  useEffect(() => {
    if (upload.state.kind === "ready") {
      props.onBannerReady(upload.state.mediaId);
    }
    // `onBannerReady` is deliberately left out of the dependency list: this
    // effect should fire once per transition into "ready", and including it
    // would re-run the effect whenever the caller passes a new closure
    // identity. This repo's lint config does not enable the react-hooks
    // rules, so there is no `exhaustive-deps` rule to suppress here.
  }, [upload.state]);

  const resolvedImageUrl =
    upload.state.kind === "ready" || upload.state.kind === "pendingReview"
      ? upload.state.previewUrl
      : props.currentBannerUrl;

  const buttonLabel =
    resolvedImageUrl !== null
      ? POOL_BANNER_CHANGE_LABEL
      : POOL_BANNER_ADD_LABEL;

  const showRemove =
    props.onRemove !== undefined && props.currentBannerUrl !== null;

  return (
    <div className="flex flex-col gap-2" data-testid={props.testId}>
      <label className="text-body-strong text-ink">{POOL_BANNER_LABEL}</label>
      {resolvedImageUrl !== null ? (
        <div
          className="aspect-[3/1] w-full overflow-hidden rounded-md"
          data-testid={`${props.testId}-preview`}
        >
          <img
            src={resolvedImageUrl}
            alt=""
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        <div
          className="flex aspect-[3/1] w-full items-center justify-center rounded-md border border-dashed border-line bg-surface-sunken px-4 text-center text-caption text-ink-secondary"
          data-testid={`${props.testId}-empty`}
        >
          {POOL_BANNER_EMPTY_MESSAGE}
        </div>
      )}
      {upload.state.kind === "failed" ? (
        // The hook resolves an oversized file to its own sentence and hands
        // it over on `state.message` (ISSUE-839); every other failure
        // arrives with `message` null and falls back to the generic line.
        // Neither is a moderation verdict (ISSUE-944): a scanned banner is
        // published and used, and staff take it down from the queue later.
        <div data-testid={`${props.testId}-failed`}>
          <InlineBanner
            variant="warning"
            message={upload.state.message ?? POOL_BANNER_UPLOAD_FAILED_MESSAGE}
          />
        </div>
      ) : upload.state.kind === "pendingReview" ? (
        <div data-testid={`${props.testId}-pending`}>
          <InlineBanner
            variant="neutral"
            message={
              props.currentBannerUrl !== null
                ? POOL_BANNER_PENDING_WITH_BANNER_MESSAGE
                : POOL_BANNER_PENDING_WITH_NONE_MESSAGE
            }
          />
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          loading={upload.state.kind === "uploading"}
          loadingLabel={POOL_BANNER_UPLOADING_LABEL}
          disabled={props.isSaving === true}
          data-testid={`${props.testId}-pick`}
          onClick={() => {
            fileInputRef.current?.click();
          }}
        >
          {buttonLabel}
        </Button>
        {showRemove ? (
          <Button
            variant="ghost"
            disabled={props.isSaving === true}
            data-testid={`${props.testId}-remove`}
            onClick={() => {
              props.onRemove?.();
            }}
          >
            {POOL_BANNER_REMOVE_LABEL}
          </Button>
        ) : null}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        className="hidden"
        data-testid={`${props.testId}-file-input`}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Clearing lets the same file fire a change again.
          event.target.value = "";
          if (file === undefined) return;
          if (
            shouldOpenCropEditor({
              file,
              contentTypes: POOL_BANNER_CONTENT_TYPES,
              maxBytes: MAX_POOL_BANNER_BYTES,
            })
          ) {
            setPendingFile(file);
            return;
          }
          upload.uploadBanner(file);
        }}
      />
      <ImageCropEditor
        file={pendingFile}
        title={POOL_BANNER_CROP_TITLE}
        aspect={POOL_BANNER_CROP_ASPECT}
        shape="rect"
        maxOutputEdgePx={CROP_OUTPUT_MAX_EDGE_PX}
        onCancel={() => {
          setPendingFile(null);
        }}
        onConfirm={(cropped) => {
          setPendingFile(null);
          upload.uploadBanner(cropped);
        }}
        testId={`${props.testId}-crop`}
      />
    </div>
  );
};
